import { addDoc, collection, doc, Timestamp, updateDoc } from "firebase/firestore";
import { Timestamp as LiteTimestamp, where as liteWhere } from "firebase/firestore/lite";
import db from "../Firebase.js";
import { Transaction } from "../../models/User.tsx";
import { Users } from "../../models/User.tsx";
import { requestAgentSummaryRefresh } from "../../helpers/agentSummaryRefresh.tsx";
import {
  chunkArray,
  getCustomerUserNamesFromRoster,
  resolveHierarchyRoster,
} from "../../helpers/hierarchyRoster.ts";
import {
  addAdminFiguresCalendarDays,
  getAdminFiguresDayStart,
} from "../../helpers/adminFiguresTime.tsx";
import { getLiteQueryDocs } from "../../helpers/firestoreLiteQuery.ts";

const ADJUSTMENT_TX_TYPES = new Set(['CreditAdjustment', 'DebitAdjustment']);

export function isFreePlayTransaction(transaction: Transaction): boolean {
  return (
    transaction.transactionFree === true ||
    String(transaction.description ?? '').toLowerCase().includes('free play')
  );
}

export async function addTransaction(newTransaction: Transaction): Promise<void> {
  const transactionRef = collection(db, 'transaction');
  await addDoc(transactionRef, newTransaction);
  if (ADJUSTMENT_TX_TYPES.has(newTransaction.transactionType)) {
    requestAgentSummaryRefresh();
  }
}

async function resolveCustomerUserNames(
  userName: string,
  userType: string,
  specificUser?: string,
  customerUserNames?: string[]
): Promise<string[]> {
  if (specificUser) {
    return [specificUser];
  }
  if (customerUserNames && customerUserNames.length > 0) {
    return customerUserNames;
  }

  const customer: string[] = [];

  if (userType === 'masterAgent') {
    const agentsSnapshot = await getLiteQueryDocs('users', [
      liteWhere('parent', '==', userName),
      liteWhere('userType', '==', 'subAgent'),
    ]);
    const agentNames = agentsSnapshot.map(d => (d.data() as Users).userName);

    customer.push(...agentNames);

    if (agentNames.length > 0) {
      const chunks = chunkArray(agentNames, 10);
      const playerSnapshots = await Promise.all(
        chunks.map(chunk =>
          getLiteQueryDocs('users', [
            liteWhere('parent', 'in', chunk),
            liteWhere('userType', '==', 'Player'),
          ])
        )
      );

      playerSnapshots.forEach(docs => {
        docs.forEach(d => {
          customer.push((d.data() as Users).userName);
        });
      });
    }
  } else if (userType === 'subAgent') {
    const playersSnapshot = await getLiteQueryDocs('users', [
      liteWhere('parent', '==', userName),
      liteWhere('userType', '==', 'Player'),
    ]);
    customer.push(...playersSnapshot.map(docSnap => (docSnap.data() as Users).userName));
  }

  return customer;
}

function applyTransactionScopeFilter(
  transactions: Transaction[],
  transactionType: string
): Transaction[] {
  if (transactionType === 'deletedTransactions') {
    return transactions.filter(transaction => transaction.status === 'Deleted');
  }

  let filtered = transactions.filter(transaction => !transaction.hasOwnProperty('status'));

  if (transactionType === 'freePlayTransactions') {
    return filtered.filter(isFreePlayTransaction);
  }

  filtered = filtered.filter(transaction => !isFreePlayTransaction(transaction));

  if (transactionType === 'playersTransaction') {
    return filtered.filter(transaction => transaction.userType === 'Player');
  }
  if (transactionType === 'agentsTransaction') {
    return filtered.filter(transaction => transaction.userType === 'subAgent');
  }

  throw new Error('error');
}

export async function searchTransactionsUsers(
  userName: string,
  startDate: string,
  endDate: string,
  userType: string,
  transactionType: string,
  specificUser?: string,
  customerUserNames?: string[]
): Promise<Transaction[]> {
  try {
    const start = LiteTimestamp.fromDate(new Date(startDate));
    const end = LiteTimestamp.fromDate(new Date(endDate));

    let customer: string[] = [];
    if (specificUser) {
      customer = [specificUser];
    } else if (customerUserNames && customerUserNames.length > 0) {
      customer = customerUserNames;
    } else if (userType === 'masterAgent' || userType === 'subAgent') {
      const roster = await resolveHierarchyRoster({
        userName,
        userType: userType as 'masterAgent' | 'subAgent',
      });
      customer = getCustomerUserNamesFromRoster(roster, userType === 'masterAgent');
    }

    if (customer.length === 0) return [];

    // Firestore solo permite 'in' con hasta 30 valores; chunkamos las consultas
    // de transacciones y las paralelizamos.
    const customerChunks = chunkArray(customer, 30);
    const txDocs = await Promise.all(
      customerChunks.map(chunk =>
        getLiteQueryDocs('transaction', [
          liteWhere('createDate', '>=', start),
          liteWhere('createDate', '<=', end),
          liteWhere('customer', 'in', chunk),
        ])
      )
    );

    const transactions: Transaction[] = txDocs.flat().map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as Transaction));

    return applyTransactionScopeFilter(transactions, transactionType);
  } catch (error) {
    throw error;
  }
}

/** Busca transacciones por ticket (prioridad sobre fechas/usuario). Limita al árbol del agente. */
export async function searchTransactionsByTicket(
  ticketQuery: string,
  userName: string,
  userType: string,
  transactionType: string,
  customerUserNames?: string[]
): Promise<Transaction[]> {
  try {
    const ticket = ticketQuery.trim();
    if (!ticket) return [];

    const ticketValues: Array<string | number> = [ticket];
    if (/^\d+$/.test(ticket)) {
      ticketValues.push(Number(ticket));
    }

    const txDocs = await Promise.all(
      ticketValues.map(value =>
        getLiteQueryDocs('transaction', [
          liteWhere('ticket', '==', value),
        ])
      )
    );

    const seen = new Set<string>();
    const transactions: Transaction[] = [];
    txDocs.flat().forEach(docSnap => {
      if (seen.has(docSnap.id)) return;
      seen.add(docSnap.id);
      transactions.push({ id: docSnap.id, ...docSnap.data() } as Transaction);
    });

    const allowedCustomers = new Set(
      await resolveCustomerUserNames(userName, userType, undefined, customerUserNames)
    );

    const scoped = transactions.filter(tx => allowedCustomers.has(tx.customer));
    return applyTransactionScopeFilter(scoped, transactionType);
  } catch (error) {
    throw error;
  }
}

export async function searchSub(queryText: string, userType: string, parent?: string, searchBy?: string): Promise<Users[]> {
  try {
    if (!parent || (userType !== 'masterAgent' && userType !== 'subAgent')) {
      return [];
    }

    const roster = await resolveHierarchyRoster({
      userName: parent,
      userType: userType as 'masterAgent' | 'subAgent',
    });

    let users: Users[] = [];
    if (userType === 'masterAgent') {
      if (searchBy === 'subAgent') users = roster.subAgents;
      if (searchBy === 'Player') users = roster.players;
    } else if (searchBy === 'Player') {
      users = roster.players;
    }

    const text = queryText.toLowerCase().trim();

    return users.filter(user => {
      const userName = user.userName?.toLowerCase() || "";
      const name = user.name?.toLowerCase() || "";
      const pin = user.pin?.toString() || "";

      return (
        userName.includes(text) ||
        name.includes(text) ||
        pin.includes(text)
      );
    });
  } catch (error) {
    throw error;
  }
}

export async function getUsersByRole(userType: string, parent?: string): Promise<{ agents: Users[]; players: Users[] }> {
  try {
    if (!parent || (userType !== 'masterAgent' && userType !== 'subAgent')) {
      return { agents: [], players: [] };
    }

    const roster = await resolveHierarchyRoster({
      userName: parent,
      userType: userType as 'masterAgent' | 'subAgent',
    });

    return {
      agents: roster.subAgents,
      players: roster.players,
    };
  } catch (error) {
    console.error("Error getting users:", error);
    throw error;
  }
}

export type UserTransactionDays = 7 | 30 | 90 | 'all';

export async function showUserTransaccion(
  userName: string,
  days: UserTransactionDays = 7
): Promise<Transaction[]> {
  try {
    const constraints = [liteWhere('customer', '==', userName)];

    if (days !== 'all') {
      const since = addAdminFiguresCalendarDays(getAdminFiguresDayStart(new Date()), -days);
      constraints.push(liteWhere('createDate', '>=', LiteTimestamp.fromDate(since)));
    }

    const docs = await getLiteQueryDocs('transaction', constraints);

    const transactions = docs
      .map(docSnap => ({ id: docSnap.id, ...(docSnap.data() as Transaction) }))
      .filter(tx => !(tx.transactionFree === true));

    return transactions;
  } catch (error) {
    throw error;
  }
}

export async function showUserFreePlayTransactions(userName: string): Promise<Transaction[]> {
  try {
    const docs = await getLiteQueryDocs("transaction", [
      liteWhere("customer", "==", userName),
      liteWhere("transactionFree", "==", true),
    ]);

    const transactions = docs
      .map(docSnap => ({
        id: docSnap.id,
        ...(docSnap.data() as Transaction),
      }))
      .filter(tx => tx.status !== "Deleted");

    return transactions;

  } catch (error) {
    throw error;
  }
}


export async function deleteTransaction(transactionId: string, deletedBy: string): Promise<void> {
  try {
    const dateDeleted = Timestamp.now();
    const transactionRef = doc(db, 'transaction', transactionId);

    await updateDoc(transactionRef, {
      dateDeleted,
      deletedBy,
      status: 'Deleted',
    });
  } catch (error) {
    throw error;
  }
}

export async function searchTransactionsByWeek(userName: string): Promise<Transaction[]> {
  try {
    let customer: string[] = [];

    const userDocs = await getLiteQueryDocs('users', [
      liteWhere('userName', '==', userName),
    ]);
    const user = userDocs[0]?.data() as Users;

    if (!user) {
      throw new Error('Usuario no encontrado');
    }

    if (user.userType === 'masterAgent' || user.userType === 'subAgent') {
      const roster = await resolveHierarchyRoster(user);
      customer = getCustomerUserNamesFromRoster(roster, user.userType === 'masterAgent');
    }

    if (customer.length === 0) return [];

    const customerChunks = chunkArray(customer, 30);
    const txDocs = await Promise.all(
      customerChunks.map(chunk =>
        getLiteQueryDocs('transaction', [
          liteWhere('customer', 'in', chunk),
          liteWhere('description', 'in', ['Customer Deposit', 'Customer Withdrawal']),
        ])
      )
    );

    const transactions: Transaction[] = txDocs.flat().map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as Transaction));

    return transactions;
  } catch (error) {
    throw error;
  }
}
